/**
 * Backend Connection Hook
 *
 * Manages backend health check with retry logic and auto-recovery.
 *
 * Behavior:
 * - On mount: bounded retries (handles uvicorn startup race and Cloud Run cold starts)
 * - When disconnected: periodic auto-recovery probe, so the banner self-heals
 *   without requiring the user to refresh the page
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { VideoGenerationAPI } from '../services/api';
import { useTranslation } from './useTranslation';
import { handleError } from '../utils/errorHandler';
import { useToast } from './useToast';
import { isServerlessMode } from '../utils/apiMode';
import { POLLING_INTERVALS, RETRY_CONFIGS } from '../constants/timings';

interface BackendConnectionState {
  isConnected: boolean | null;
  isChecking: boolean;
  error: string | null;
}

interface UseBackendConnectionReturn extends BackendConnectionState {
  retryConnection: () => void;
}

const { maxRetries: MAX_RETRIES, delayMs: RETRY_DELAY_MS } = RETRY_CONFIGS.STANDARD;
const AUTO_RECOVERY_INTERVAL_MS = POLLING_INTERVALS.HEALTH_CHECK;

export function useBackendConnection(): UseBackendConnectionReturn {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recoveryIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [state, setState] = useState<BackendConnectionState>({
    isConnected: null,
    isChecking: true,
    error: null,
  });

  const checkBackend = useCallback(
    async (retryCount = 0, showToasts = true) => {
      setState((prev) => ({ ...prev, isChecking: true, error: null }));

      try {
        const response = await VideoGenerationAPI.checkHealth();

        if (response.success) {
          setState({
            isConnected: true,
            isChecking: false,
            error: null,
          });

          // Only show success toast if it was a retry (recovered from cold start
          // or backend startup race)
          if (showToasts && retryCount > 0) {
            showToast(
              'success',
              t('app.notifications.backendConnectedTitle'),
              t('app.notifications.backendConnectedMessage')
            );
          }
          return;
        }

        // Retry transparently for both Cloud Run cold starts and local uvicorn
        // startup races — local backends can also be slow on first launch.
        if (retryCount < MAX_RETRIES) {
          retryTimeoutRef.current = setTimeout(
            () => checkBackend(retryCount + 1, showToasts),
            RETRY_DELAY_MS
          );
          return;
        }

        setState({
          isConnected: false,
          isChecking: false,
          error: response.error || t('app.notifications.backendUnavailableMessage'),
        });

        // Only show error in local mode (not serverless environments)
        if (showToasts && !isServerlessMode()) {
          showToast(
            'error',
            t('app.notifications.backendUnavailableTitle'),
            response.error || t('app.notifications.backendUnavailableMessage')
          );
        }
      } catch (error) {
        if (retryCount < MAX_RETRIES) {
          retryTimeoutRef.current = setTimeout(
            () => checkBackend(retryCount + 1, showToasts),
            RETRY_DELAY_MS
          );
          return;
        }

        const errorMessage = handleError(
          error,
          'BackendConnection',
          t('app.notifications.connectionFailedMessage')
        );

        setState({
          isConnected: false,
          isChecking: false,
          error: errorMessage,
        });

        if (showToasts && !isServerlessMode()) {
          showToast(
            'error',
            t('app.notifications.connectionFailedTitle'),
            t('app.notifications.connectionFailedMessage')
          );
        }
      }
    },
    [showToast, t]
  );

  useEffect(() => {
    checkBackend();
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (recoveryIntervalRef.current) {
        clearInterval(recoveryIntervalRef.current);
      }
    };
  }, [checkBackend]);

  // Auto-recover: while disconnected, probe periodically so the banner clears
  // itself once the backend comes back up — no manual refresh required.
  useEffect(() => {
    if (recoveryIntervalRef.current) {
      clearInterval(recoveryIntervalRef.current);
      recoveryIntervalRef.current = null;
    }

    if (state.isConnected !== false) {
      return;
    }

    recoveryIntervalRef.current = setInterval(() => {
      checkBackend(0, false);
    }, AUTO_RECOVERY_INTERVAL_MS);

    return () => {
      if (recoveryIntervalRef.current) {
        clearInterval(recoveryIntervalRef.current);
        recoveryIntervalRef.current = null;
      }
    };
  }, [state.isConnected, checkBackend]);

  const retryConnection = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
    checkBackend(0, true);
  }, [checkBackend]);

  return {
    ...state,
    retryConnection,
  };
}
